import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Block, LabelledInput } from './PrimaNotaBlocks';
import type { DeclarationOnlyItem } from './prima-nota-model';

type DeclarationRowProps = {
  item: DeclarationOnlyItem;
  index: number;
  onEdit: (index: number, patch: Partial<DeclarationOnlyItem>) => void;
  onRemove: (index: number) => void;
};

function DeclarationRow({ item, index, onEdit, onRemove }: DeclarationRowProps) {
  const { t } = useTranslation('finance');
  return (
    <li className="bg-background border-border space-y-3 rounded-md border p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <LabelledInput
          id={`declaration-date-${index}`}
          label={t('primaNota.declarations.date')}
          value={item.date}
          onChange={(value) => onEdit(index, { date: value })}
        />
        <LabelledInput
          id={`declaration-counterparty-${index}`}
          label={t('primaNota.declarations.counterparty')}
          value={item.counterparty}
          onChange={(value) => onEdit(index, { counterparty: value })}
        />
        <LabelledInput
          id={`declaration-ref-${index}`}
          label={t('primaNota.declarations.ref')}
          value={item.ref}
          onChange={(value) => onEdit(index, { ref: value })}
        />
        <LabelledInput
          id={`declaration-amount-${index}`}
          label={t('primaNota.declarations.amount')}
          value={item.amount}
          onChange={(value) => onEdit(index, { amount: value })}
        />
      </div>
      <LabelledInput
        id={`declaration-treatment-${index}`}
        label={t('primaNota.declarations.treatment')}
        value={item.treatment}
        onChange={(value) => onEdit(index, { treatment: value })}
      />
      <Button variant="ghost" size="sm" onClick={() => onRemove(index)}>
        {t('primaNota.declarations.remove')}
      </Button>
    </li>
  );
}

type DeclarationsBlockProps = {
  declarations: DeclarationOnlyItem[];
  onAdd: () => void;
  onEdit: (index: number, patch: Partial<DeclarationOnlyItem>) => void;
  onRemove: (index: number) => void;
};

/** The costs the operator declares by hand, which no document or statement carries. */
export function DeclarationsBlock({
  declarations,
  onAdd,
  onEdit,
  onRemove,
}: DeclarationsBlockProps) {
  const { t } = useTranslation('finance');
  return (
    <Block
      id="prima-nota-declarations-heading"
      title={t('primaNota.declarations.heading')}
      testId="prima-nota-declarations"
      aside={
        <Button
          variant="outline"
          size="sm"
          data-testid="prima-nota-add-declaration"
          onClick={onAdd}
        >
          <Plus aria-hidden="true" />
          {t('primaNota.declarations.add')}
        </Button>
      }
    >
      <p className="text-muted-foreground text-[13px] leading-5">
        {t('primaNota.declarations.hint')}
      </p>
      {declarations.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('primaNota.declarations.none')}</p>
      ) : (
        <ul className="space-y-3">
          {declarations.map((item, index) => (
            <DeclarationRow
              key={index}
              item={item}
              index={index}
              onEdit={onEdit}
              onRemove={onRemove}
            />
          ))}
        </ul>
      )}
    </Block>
  );
}
